import { z } from 'zod'

import { bsDateSchema, codeParamSchema, moneySchema } from '@/schemas/common'
import { tableQuerySchema } from '@/schemas/table'

/**
 * Market · Floorsheet — one day's executed trades: a summary of the session, the
 * trade log itself, and two panels on where the money went.
 *
 * Broker numbers are strings throughout: NEPSE writes them "07", and a number
 * would print as 7 in the one column where the leading zero is the identifier.
 */

/** "Today across NEPSE" — the four figures above the table. */
export const floorsheetSummarySchema = z.object({
  date: bsDateSchema,
  tradesExecuted: z.number().int(),
  /** Kitta in crore: "1.42 Cr". */
  kittaTradedCr: moneySchema,
  /** Rupees in arba: "4.82 arba". */
  valueTradedArba: moneySchema,
  companiesTraded: z.number().int(),
})

export const tradeRowSchema = z.object({
  id: z.string().min(1),
  symbol: z.string().min(1),
  name: z.string().min(1),
  /**
   * Wall clock with seconds, as the floorsheet stamps it: "3:00:41 PM" — or the
   * em-dash, for a contract NEPSE published without one.
   *
   * `FloorsheetTrade.trade_time` and `BrokerTrade.trade_time` are both nullable
   * on the wire (`src/api/schema.d.ts`), and `displayTimeWithSeconds` answers a
   * null with '—' exactly as the Buyer and Seller columns do. Without the second
   * branch here one unstamped contract ZodErrors the page it arrives on and the
   * whole table renders its error state — the opposite of what a per-cell gap
   * should cost.
   */
  time: z.union([
    z.string().regex(/^\d{1,2}:\d{2}:\d{2} [AP]M$/, 'Expected a trade time, e.g. "3:00:41 PM"'),
    z.literal('—'),
  ]),
  /** Buying broker number. */
  buyer: z.string().min(1),
  seller: z.string().min(1),
  kitta: z.number().int(),
  rate: moneySchema,
  amount: moneySchema,
})

/**
 * The two panels under the table, both about TODAY: `/market/brokers/` and
 * `/market/movers/` take no date, so a past day draws neither (see
 * `FLOORSHEET_PAST_DAY_PANELS_AVAILABLE`).
 */
export const floorsheetInsightsSchema = z.object({
  /** The five brokers that handled the most rupees, as a share of all trading. */
  busiestBrokers: z.object({
    rows: z.array(
      z.object({ code: z.string().min(1), name: z.string().min(1), percent: moneySchema }),
    ),
    /** The five together, as a share of all trading: "these five handled 38%". */
    totalPercent: moneySchema,
  }),
  /** The day's five most traded shares, ranked by rupees. */
  mostTraded: z.array(
    z.object({
      symbol: z.string().min(1),
      /** Kitta that changed hands. */
      kitta: z.number().int().nonnegative(),
      /** `null` when mm-be published no turnover for the name (`MoverRow.turnover` is nullable). */
      valueRs: moneySchema.nullable(),
    }),
  ),
})

/** One row of the Buyer / Seller menus. */
export const floorsheetBrokerOptionSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
})

/** Symbol and broker params go through `codeParamSchema` (schemas/common.ts): a bare `?broker=58` parses as a number. */
export const floorsheetQuerySchema = tableQuerySchema.extend({
  /** Narrow to one listed name. Absent means "All symbols". */
  symbol: codeParamSchema.optional(),
  /** Trades this broker number BOUGHT in. The V2 "Buyer" field. */
  buyer: codeParamSchema.optional(),
  /** Trades this broker number SOLD in. The V2 "Seller" field. */
  seller: codeParamSchema.optional(),
  /**
   * Trades this broker was on EITHER side of. No field on the screen draws it
   * any more — Buyer and Seller replaced it — but it stays a valid param so
   * the "Open floorsheet" link on a broker's page (`?broker=58`, brokers
   * family) keeps landing on that broker's trades rather than on the whole
   * day. The screen drops it as soon as the member picks a buyer or seller.
   */
  broker: codeParamSchema.optional(),
})

/**
 * The orders `/market/floorsheet/` serves (`order=recent|amount|amount_asc`): newest first,
 * and by rupees either way. Quantity, rate and oldest-first are not served (asks/tr1.md).
 */
export type FloorsheetOrder = 'recent' | 'amount' | 'amount_asc'

/** The table's sort state, as the URL holds it, to the endpoint's `order`. Anything else is `recent`. */
export const floorsheetOrder = (
  sortBy: string | undefined,
  sortDesc: boolean | undefined,
): FloorsheetOrder =>
  sortBy === 'amount' ? (sortDesc === false ? 'amount_asc' : 'amount') : 'recent'

export type FloorsheetSummary = z.infer<typeof floorsheetSummarySchema>
export type FloorsheetBrokerOption = z.infer<typeof floorsheetBrokerOptionSchema>
export type TradeRow = z.infer<typeof tradeRowSchema>
export type FloorsheetInsights = z.infer<typeof floorsheetInsightsSchema>
export type FloorsheetQuery = z.infer<typeof floorsheetQuerySchema>
